"use client"
import { useForm } from "react-hook-form"
import { useState } from "react"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Textarea } from "@/shared/components/ui/textarea"
import { Calendar } from "@/shared/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/components/ui/form"
import { MoveRight, CalendarDays } from "lucide-react"
import { cn } from "@/shared/lib/utils/UtilsExports"
import { format } from "date-fns"
import type { BasicInformationFormData } from "@/shared/types/Forms"
import { useToast } from "@/shared/components/Toast"

export const BasicInformationForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { showToast } = useToast()

  const basicForm = useForm<BasicInformationFormData>({
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      nationality: "",
      destination: "",
      numberofpax: "",
      travelDate: "",
      purpose: "",
      additionalInfo: "",
      bestTimeToCall: "",
    },
  })

  const onBasicSubmit = async (data: BasicInformationFormData) => {
    setIsSubmitting(true)

    try {
      const payload = {
        applicant_name: data.fullName,
        email: data.email,
        phone: data.phone,
        nationality: data.nationality || '',
        destination: data.destination,
        number_of_pax: data.numberofpax,
        travel_dates: data.travelDate || '',
        purpose: data.purpose || '',
        additional_info: data.additionalInfo || '',
        best_time_to_call: data.bestTimeToCall || '',
      }
      const response = await fetch('/api/v2/forms.php?type=visa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const result = await response.json()

      if (result.success) {
        // Show toast notification
        showToast({
          type: 'success',
          title: 'Application Submitted!',
          message: 'Your visa application has been submitted successfully. We\'ll contact you soon!',
          duration: 5000
        })
        
        basicForm.reset()
      } else {
        // Show error toast
        showToast({
          type: 'error',
          title: 'Submission Failed',
          message: result.error || result.message || 'Failed to submit visa application. Please try again.',
          duration: 5000
        })
      }
    } catch (error) {
      console.error('Visa application error:', error)
      const errorMessage = 'Failed to submit visa application. Please try again later.'
      
      // Show error toast
      showToast({
        type: 'error',
        title: 'Error',
        message: errorMessage,
        duration: 5000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...basicForm}>
      <form onSubmit={basicForm.handleSubmit(onBasicSubmit)} className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={basicForm.control}
            name="fullName"
            rules={{ required: "Full name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Full Name *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="John Doe"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={basicForm.control}
            name="email"
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Email Address *</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={basicForm.control}
            name="phone"
            rules={{ required: "Phone number is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Phone Number *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="+63 912 345 6789"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Removed Best Time to Call field by request */}
            <FormField
              control={basicForm.control}
              name="bestTimeToCall"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-700">Best Time to Call</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., 3:30 PM"
                      className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={basicForm.control}
            name="nationality"
            rules={{ required: "Nationality is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Nationality *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Philippines"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={basicForm.control}
            name="destination"
            rules={{ required: "Destination is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Destination *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Japan"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            control={basicForm.control}
            name="travelDate"
            rules={{ required: "Travel date is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Intended Travel Date *</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full h-11 justify-start text-left font-normal border-gray-300",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        <CalendarDays className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{field.value ? format(new Date(field.value), "PPP") : "Pick a date"}</span>
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ? new Date(field.value) : undefined}
                      onSelect={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={basicForm.control}
            name="numberofpax"
            rules={{ required: "Number of pax is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold text-gray-700">Number of Pax *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="10 pax"
                    className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={basicForm.control}
          name="purpose"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-gray-700">Purpose of Travel</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g., Tourism"
                  className="h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={basicForm.control}
          name="additionalInfo"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-gray-700">Additional Information</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any additional information or special requirements..."
                  rows={5}
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-2">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            variant="ghost"
            className="btn-autumn-primary h-12 font-semibold px-8 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)', color: 'white' }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = 'rgb(234 88 12)'
                e.currentTarget.style.color = 'white'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(249 115 22)'
              e.currentTarget.style.color = 'white'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
            <MoveRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </form>
    </Form>
  )
}
